/**
 * Admin authentication store.
 *
 * Manages JWT token storage/retrieval and user state.
 * Token is stored in httpOnly cookie (set by server) and sent automatically by browser.
 */

import { create } from 'zustand';

function getCookieValue(cookieHeader: string | null, key: string): string | null {
  if (cookieHeader === null || cookieHeader === '') {
    return null;
  }
  const cookies = cookieHeader.split(';').map((c) => c.trim());
  const tokenCookie = cookies.find((c) => c.startsWith(`${key}=`));
  return tokenCookie !== undefined ? (tokenCookie.split('=')[1] ?? null) : null;
}

interface AuthUser {
  id: number;
  email: string;
  role: string;
  accountId: number;
}

interface LoginResponse {
  user: AuthUser;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  verify: () => Promise<void>;
}

const initialToken =
  typeof document !== 'undefined' ? getCookieValue(document.cookie, 'merritrac-admin-token') : null;

export const useAuth = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: initialToken !== null,
  error: null,

  login: async (email: string, password: string): Promise<boolean> => {
    set({ isLoading: true, error: null });

    try {
      const res = await fetch('/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      if (!res.ok) {
        const body = (await res.json()) as { message?: string };
        set({
          isLoading: false,
          error: body.message ?? 'Login failed',
          isAuthenticated: false,
        });
        return false;
      }

      const data = (await res.json()) as LoginResponse;

      set({
        user: data.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      return true;
    } catch {
      set({ isLoading: false, error: 'Network error', isAuthenticated: false });
      return false;
    }
  },

  logout: (): void => {
    // Clear the cookie by setting it to expire
    document.cookie = 'merritrac-admin-token=; Max-Age=0; path=/';
    set({ user: null, isAuthenticated: false, error: null });
  },

  verify: async (): Promise<void> => {
    set({ isLoading: true });

    try {
      const res = await fetch('/admin/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!res.ok) {
        document.cookie = 'merritrac-admin-token=; Max-Age=0; path=/';
        set({ user: null, isAuthenticated: false, isLoading: false });
        return;
      }

      const data = (await res.json()) as { user: AuthUser };
      set({ user: data.user, isAuthenticated: true, isLoading: false });
    } catch {
      document.cookie = 'merritrac-admin-token=; Max-Age=0; path=/';
      set({ user: null, isLoading: false, isAuthenticated: false });
    }
  },
}));
